import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { resolveUserRoleLabel } from "../../Utils/resolveUserRoleLabel";
import { formatDateTime } from "../../Utils/walletFormat";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";

function displayValue(value) {
  if (value == null || String(value).trim() === "") return "";
  return String(value);
}

function MyProfilePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { userData, login } = useAuthStore();

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone_number: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      full_name: displayValue(userData?.full_name),
      email: displayValue(userData?.email),
      phone_number: displayValue(
        userData?.phone_number || userData?.phone
      ),
    });
  }, [userData]);

  const organizationName = displayValue(
    userData?.organization?.organization_name ||
      userData?.user_active_organization?.organization?.organization_name
  );
  const role = resolveUserRoleLabel(userData);
  const lastLogin = formatDateTime(
    userData?.last_login_at || userData?.last_login
  );
  const applicationUserId = userData?.application_user_id;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === "phone_number" ? value.replace(/\D/g, "").slice(0, 15) : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.full_name.trim()) {
      errors.full_name = "Full name is required.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim()) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(form.email.trim())) {
      errors.email = "Enter a valid email address.";
    }

    if (!form.phone_number.trim()) {
      errors.phone_number = "Phone is required.";
    } else if (!/^\d{7,15}$/.test(form.phone_number.trim())) {
      errors.phone_number = "Phone must be 7–15 digits.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!applicationUserId) {
      toast.error("Unable to update profile: missing user id.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone_number: form.phone_number.trim(),
      };

      const { data } = await axios.put(
        `${MAIN_URL}/api/application/user/${applicationUserId}`,
        payload,
        { headers: getApiHeaders() }
      );

      const updatedUser = data?.users || {};
      login({
        ...userData,
        full_name: updatedUser.full_name ?? payload.full_name,
        email: updatedUser.email ?? payload.email,
        phone_number: updatedUser.phone_number ?? payload.phone_number,
        last_login_at:
          updatedUser.last_login_at ?? userData?.last_login_at,
      });

      setFormErrors({});
      toast.success("Profile updated successfully.");
    } catch (err) {
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        const normalized = {};
        Object.entries(validationErrors).forEach(([key, value]) => {
          normalized[key] = Array.isArray(value) ? value[0] : String(value);
        });
        setFormErrors(normalized);
        const firstMessage = Object.values(normalized)[0];
        toast.error(firstMessage || "Validation failed.");
      } else {
        toast.error(
          err.response?.data?.error || "Something went wrong."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box px={isMobile ? 2 : 5} py={4} maxWidth={640}>
      <Typography variant="h5" fontWeight={600} mb={1}>
        My Profile
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Update your account details. Organization and role are managed by admin.
      </Typography>

      <Paper
        component="form"
        onSubmit={handleSave}
        elevation={0}
        variant="outlined"
        sx={{ p: 3 }}
      >
        <TextField
          fullWidth
          label="Full name"
          name="full_name"
          value={form.full_name}
          onChange={handleChange}
          margin="normal"
          error={!!formErrors.full_name}
          helperText={formErrors.full_name}
        />
        <TextField
          fullWidth
          label="Email"
          name="email"
          value={form.email}
          onChange={handleChange}
          margin="normal"
          error={!!formErrors.email}
          helperText={formErrors.email}
        />
        <TextField
          fullWidth
          label="Phone"
          name="phone_number"
          value={form.phone_number}
          onChange={handleChange}
          margin="normal"
          error={!!formErrors.phone_number}
          helperText={formErrors.phone_number}
        />
        <TextField
          fullWidth
          label="Organization"
          value={organizationName}
          margin="normal"
          InputProps={{ readOnly: true }}
        />
        <TextField
          fullWidth
          label="Role"
          value={role}
          margin="normal"
          InputProps={{ readOnly: true }}
        />
        <TextField
          fullWidth
          label="Last login"
          value={lastLogin === "—" ? "" : lastLogin}
          margin="normal"
          InputProps={{ readOnly: true }}
        />

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !applicationUserId}
          >
            {saving ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Save changes"
            )}
          </Button>
          <Link
            component={RouterLink}
            to="/settings/change-password"
            underline="hover"
            variant="body2"
          >
            Change password
          </Link>
        </Stack>
      </Paper>
    </Box>
  );
}

export default MyProfilePage;
